import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}
interface State {
  hasError: boolean;
  error?: Error;
}

/** Catches render-time crashes so one broken feature can't white-screen the app. */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // TODO: forward to Sentry once VITE_SENTRY_DSN is set.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <div className="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
            <h1 className="text-xl font-bold text-hi">Something went wrong.</h1>
            <p className="max-w-sm text-sm text-mid">
              The page hit an unexpected error. Try reloading — if it keeps happening, we&apos;re on
              it.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 rounded-pill bg-accent px-5 py-2 text-sm font-semibold text-black"
            >
              Reload
            </button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
